import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listRooms, listBuildings } from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import PaginationFooter from '../components/PaginationFooter';
import { SensorReadings } from '../components/SensorReadings';
import { MovementInline } from '../components/movement/MovementInline';
import { useMovementLive } from '../lib/movement';
import { useNightPolicy, useMovementBuckets } from '../lib/movementView';
import { useOrg } from '../contexts/OrgContext';
import {
  DoorOpen, WindowsLogo, UsersThree, WifiHigh, ArrowRight, MagnifyingGlass, FunnelSimple,
} from '@phosphor-icons/react';

const ROOM_TYPES = ['private_room', 'shared_room', 'apartment', 'corridor', 'common_area'];
const PAGE_SIZE = 50;

export default function Rooms() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingId = searchParams.get('building') || '';
  const [buildingLabel, setBuildingLabel] = useState('');

  // Resolve the building name from the buildings list so the filter banner shows
  // it even when the building has zero rooms (rooms can't supply corpus_name then).
  useEffect(() => {
    if (!buildingId) { setBuildingLabel(''); return; }
    listBuildings()
      .then((bs) => setBuildingLabel(bs.find((b) => b.id === buildingId)?.name || ''))
      .catch(() => setBuildingLabel(''));
  }, [buildingId]);
  const [rooms, setRooms] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState('');
  const [roomType, setRoomType] = useState('');
  const debounceRef = useRef(null);
  const { current } = useOrg() || {};
  const live = useMovementLive();
  const motionPolicy = useNightPolicy(current?.id);
  const motionByRoom = (live?.devices || []).reduce((m, d) => (d.assigned_room_id
    ? { ...m, [d.assigned_room_id]: [...(m[d.assigned_room_id] || []), d] } : m), {});
  const motionBuckets = useMovementBuckets({ hours: 24 }, Object.keys(motionByRoom).length > 0);

  // Initial + reactive fetch on filter change (debounced for `q`).
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      listRooms({
        limit: buildingId ? 200 : PAGE_SIZE,
        offset: 0,
        q: q.trim() || undefined,
        room_type: roomType || undefined,
      })
        .then(({ items, total }) => {
          setRooms(items);
          setTotal(total);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[Rooms] list failed:', err?.response?.status || err?.message);
          setRooms([]);
          setTotal(0);
        })
        .finally(() => setLoading(false));
    }, q ? 300 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [q, roomType, buildingId]);

  const onLoadMore = async () => {
    setLoadingMore(true);
    try {
      const { items, total } = await listRooms({
        limit: PAGE_SIZE,
        offset: rooms.length,
        q: q.trim() || undefined,
        room_type: roomType || undefined,
      });
      setRooms((prev) => [...prev, ...items]);
      setTotal(total);
    } finally {
      setLoadingMore(false);
    }
  };

  const visibleRooms = buildingId ? rooms.filter((r) => r.corpus_id === buildingId) : rooms;
  const buildingName = buildingId
    ? (buildingLabel || rooms.find((r) => r.corpus_id === buildingId)?.corpus_name || '')
    : '';

  const clearBuilding = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('building');
    setSearchParams(next);
  };

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_rooms')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('rooms_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">{t('rooms_sub')}</p>
      </header>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            placeholder={t('search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="rooms-search"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
          />
        </div>

        <div className="relative">
          <FunnelSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
          <select
            value={roomType}
            onChange={(e) => setRoomType(e.target.value)}
            data-testid="rooms-type-filter"
            className="appearance-none pl-9 pr-8 py-3 rounded-xl border border-stone_line bg-white text-sm focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 transition-all"
          >
            <option value="">{t('rooms_filter_all_types') || 'All types'}</option>
            {ROOM_TYPES.map((rt) => (
              <option key={rt} value={rt}>{t(`room_type_${rt}`)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {buildingId && (
          <div className="px-6 py-3 bg-sage-100/60 border-b border-stone_line flex items-center justify-between gap-3" data-testid="rooms-building-filter">
            <span className="text-sm text-sage-700">
              {t('admin_field_building')}: <span className="font-medium">{buildingName || '—'}</span>
            </span>
            <button
              type="button"
              onClick={clearBuilding}
              data-testid="rooms-building-clear"
              className="text-xs text-ink-soft hover:text-ink underline underline-offset-2"
            >
              {t('rooms_building_clear')}
            </button>
          </div>
        )}
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : visibleRooms.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <>
            <ul className="divide-y divide-stone_line" data-testid="rooms-list">
              {visibleRooms.map((r) => (
                <li key={r.id} className="px-6 py-5 flex items-center gap-5 hover:bg-bg-alt/40 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-sage-100 text-sage-600 grid place-items-center shrink-0">
                    <DoorOpen size={20} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-medium text-ink">
                        {t('room')} {r.room_name}
                      </div>
                      {r.room_type && (
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-muted bg-bg-alt px-2 py-0.5 rounded-full">
                          {t(`room_type_${r.room_type}`)}
                        </span>
                      )}
                      {r.floor != null && (
                        <span className="text-xs text-ink-muted">
                          {t('room_floor')} {r.floor}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-ink-soft mt-1 flex items-center gap-4 flex-wrap">
                      <span className="inline-flex items-center gap-1.5">
                        <UsersThree size={14} weight="duotone" />
                        {r.patient_count ?? 0}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <WifiHigh size={14} weight="duotone" />
                        {r.device_count ?? 0}
                      </span>
                      {(r.offline_devices ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-terracotta-700">
                          {r.offline_devices} {t('status_offline').toLowerCase()}
                        </span>
                      )}
                      {r.door_state && r.door_state !== 'unknown' && (
                        <span
                          data-testid={`room-door-${r.id}`}
                          title={`${t('door')}: ${t(`door_${r.door_state}_state`)}`}
                          className={`inline-flex items-center gap-1 ${r.door_state === 'open' ? 'text-terracotta-700' : 'text-sage-600'}`}
                        >
                          <DoorOpen size={15} weight="duotone" />
                        </span>
                      )}
                      {r.window_state && r.window_state !== 'unknown' && (
                        <span
                          data-testid={`room-window-${r.id}`}
                          title={`${t('window')}: ${t(`door_${r.window_state}_state`)}`}
                          className={`inline-flex items-center gap-1 ${r.window_state === 'open' ? 'text-terracotta-700' : 'text-sage-600'}`}
                        >
                          <WindowsLogo size={15} weight="duotone" />
                        </span>
                      )}
                      <SensorReadings
                        compact
                        temperature={r.last_temperature}
                        humidity={r.last_humidity}
                        testid={`room-readings-${r.id}`}
                      />
                      {motionByRoom[r.id] && (
                        <MovementInline devices={motionByRoom[r.id]} buckets={motionBuckets} policy={motionPolicy} testid={`room-movement-${r.id}`} />
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/rooms/${encodeURIComponent(r.id)}`}
                    data-testid={`room-view-${r.id}`}
                    className="inline-flex items-center gap-2 text-sm text-sage hover:text-sage-600"
                  >
                    {t('view_profile')} <ArrowRight size={16} weight="bold" />
                  </Link>
                </li>
              ))}
            </ul>

            {!buildingId && (
              <PaginationFooter
                shown={rooms.length}
                total={total}
                onLoadMore={onLoadMore}
                loading={loadingMore}
                testid="rooms-pagination"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
